import { memo } from 'react';
import { cn } from '@/lib/utils';

export interface QuickTapButtonProps {
  examTypeId: string;
  shortName: string;
  fullName: string;
  unitValue: number;
  count: number;
  onTap: (examTypeId: string) => void;
  onRemove?: (examTypeId: string) => void;
  disabled?: boolean;
}

function QuickTapButtonImpl({
  examTypeId,
  shortName,
  fullName,
  unitValue,
  count,
  onTap,
  onRemove,
  disabled,
}: QuickTapButtonProps) {
  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onTap(examTypeId)}
        aria-label={`${fullName} — ${count} registrados, valor unitário ${brl(unitValue)}`}
        className={cn(
          'flex min-h-16 w-full flex-col items-center justify-center rounded-lg border border-(--color-border) bg-(--color-surface-elevated) p-2 text-center font-medium shadow-sm transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent) disabled:opacity-40',
        )}
        style={{ touchAction: 'manipulation' }}
      >
        <span className="text-sm font-semibold leading-tight">{shortName}</span>
        <span className="line-clamp-2 text-xs leading-tight text-(--color-fg-muted)">{fullName}</span>
        <span className="mt-1 text-xs text-(--color-fg-muted)">{brl(unitValue)}</span>
      </button>

      {count > 0 && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-2 -right-2 flex min-w-6 items-center justify-center rounded-full bg-(--color-accent) px-1.5 py-0.5 text-xs font-bold text-(--color-bg)"
        >
          {count}
        </span>
      )}

      {count > 0 && onRemove && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onRemove(examTypeId)}
          aria-label={`Remover um ${fullName}`}
          className="absolute -top-2 -left-2 flex h-6 w-6 items-center justify-center rounded-full border border-(--color-border) bg-(--color-surface) text-base font-bold leading-none text-(--color-fg) shadow-sm transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent) disabled:opacity-40"
          style={{ touchAction: 'manipulation' }}
        >
          −
        </button>
      )}
    </div>
  );
}

function brl(v: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

export const QuickTapButton = memo(QuickTapButtonImpl);
